import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Search,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
  Home,
  Compass,
  UserCircle,
  Factory,
  Gavel,
  Target,
  AlertTriangle,
  MessageSquare,
  Megaphone,
  MapPinned,
  BookUser,
  UserRound,
  HardHat,
  Contact2,
  UserCog,
  Bot,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useProspectStats } from '../hooks/useApi'

const menuItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/my-assignments', icon: MapPinned, label: 'My Assignments' },
  { path: '/my-contacts', icon: Contact2, label: 'My Contacts' },
  { path: '/listings', icon: Compass, label: 'Listings' },
  { path: '/lbp-builders', icon: HardHat, label: 'LBP and Builders' },
  // Agents / FB Ads Leads sit above Commercial & Industrial Zones (2026-08-29
  // request) — still gated on canViewAgents via the agentsOnly flag.
  { path: '/agents', icon: UserCircle, label: 'Agents', agentsOnly: true },
  { path: '/fb-leads', icon: Megaphone, label: 'FB Ads Leads', agentsOnly: true },
  { path: '/fb-leads/ai', icon: Bot, label: 'FB Lead AI Follow-Up', agentsOnly: true },
  { path: '/industrial', icon: Factory, label: 'Commercial & Industrial Zones' },
  { path: '/prospects', icon: Target, label: 'Prospects', badge: 'prospects' },
  // The full admin SMS page lives under adminItems (isAdmin-gated) — this is
  // the entry point for everyone else with sms_access, who previously had no
  // sidebar link into SMS at all despite the route being reachable.
  { path: '/sms/my', icon: MessageSquare, label: 'My SMS', smsAccessOnly: true },
]

// Visible to admins + allow-listed salespeople (canViewAgents)
const agentItems = [
  { path: '/private-listers', icon: UserRound, label: 'Private Listers' },
]

const adminItems = [
  { path: '/admin/users', icon: UserCog, label: 'Users & Permissions' },
  { path: '/master-contacts', icon: BookUser, label: 'Master Contacts' },
  { path: '/search', icon: Search, label: 'Search' },
  { path: '/mortgagee-sales', icon: Gavel, label: 'Mortgagee Sales' },
  { path: '/sms', icon: MessageSquare, label: 'SMS' },
  { path: '/sms/campaigns', icon: Megaphone, label: 'Campaigns' },
  { path: '/admin/ambiguous-matches', icon: AlertTriangle, label: 'Ambiguous Matches' },
  { path: '/admin/lead-assignments', icon: MapPinned, label: 'Lead Assignments' },
]

function Sidebar({ isOpen, onToggle, mobile = false }) {
  const location = useLocation()
  const { isAdmin, canViewAgents, canSendSms } = useAuth()
  const { data: stats } = useProspectStats()

  const activeCount = stats?.active_requirements || 0
  const newMatchCount = stats?.new_matches || 0

  // Mobile drawer mode: always expanded, no collapse toggle, X closes it
  // (onToggle closes the drawer in that context).
  const expanded = mobile || isOpen

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-sidebar-bg text-sidebar-text transition-all duration-300 z-30 ${
        expanded ? 'w-64' : 'w-16'
      }`}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-grey-700">
        {expanded ? (
          <div className="flex items-center gap-2">
            <Home className="w-6 h-6 text-accent-500" />
            <span className="font-bold text-lg">Leadforce</span>
          </div>
        ) : (
          <Home className="w-6 h-6 text-accent-500 mx-auto" />
        )}
        <button
          onClick={onToggle}
          className="p-1 rounded-lg hover:bg-sidebar-hover transition-colors"
          aria-label={mobile ? 'Close menu' : isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={expanded}
        >
          {mobile ? (
            <X className="w-5 h-5" />
          ) : isOpen ? (
            <ChevronLeft className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </button>
      </div>
      
      {/* Navigation */}
      <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100% - 8rem)' }}>
        {menuItems.map((item) => {
          const Icon = item.icon

          if (item.agentsOnly && !canViewAgents()) return null
          if (item.smsAccessOnly && !canSendSms()) return null

          if (item.external) {
            return (
              <a
                key={item.path}
                href={item.path}
                className={`
                  sidebar-link text-grey-300 hover:bg-sidebar-hover
                  ${expanded ? '' : 'justify-center px-2'}
                `}
                title={!expanded ? item.label : ''}
                aria-label={!expanded ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {expanded && <span className="ml-3">{item.label}</span>}
              </a>
            )
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `
                sidebar-link ${isActive ? 'active bg-primary-600' : 'text-grey-300 hover:bg-sidebar-hover'}
                ${expanded ? '' : 'justify-center px-2'}
              `}
              title={!expanded ? item.label : ''}
              aria-label={!expanded ? item.label : undefined}
            >
              <div className="relative">
                <Icon className="w-5 h-5 flex-shrink-0" />
                {item.badge === 'prospects' && activeCount > 0 && !expanded && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full text-[9px] flex items-center justify-center text-white font-bold">
                    {activeCount > 9 ? '9+' : activeCount}
                  </span>
                )}
              </div>
              {expanded && (
                <span className="ml-3 flex-1">{item.label}</span>
              )}
              {item.badge === 'prospects' && activeCount > 0 && expanded && (
                <span className="ml-auto px-1.5 py-0.5 bg-red-500 rounded text-[10px] text-white font-bold min-w-[18px] text-center">
                  {activeCount}
                </span>
              )}
            </NavLink>
          )
        })}

        {/* Agents section (admins + allow-listed salespeople) */}
        {canViewAgents() && agentItems.length > 0 && (
          <>
            {!expanded && <div className="mt-2 border-t border-grey-700" />}
            {agentItems.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `
                    sidebar-link ${isActive ? 'active bg-primary-600' : 'text-grey-300 hover:bg-sidebar-hover'}
                    ${expanded ? '' : 'justify-center px-2'}
                  `}
                  title={!expanded ? item.label : ''}
                  aria-label={!expanded ? item.label : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {expanded && <span className="ml-3">{item.label}</span>}
                </NavLink>
              )
            })}
          </>
        )}

        {/* Admin Section */}
        {isAdmin() && adminItems.length > 0 && (
          <>
            {expanded && (
              <div className="pt-4 mt-2 border-t border-grey-700">
                <div className="px-3 pb-2 text-[10px] font-bold text-grey-500 uppercase tracking-wider">
                  Admin
                </div>
              </div>
            )}
            {!expanded && <div className="mt-2 border-t border-grey-700" />}
            {adminItems.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `
                    sidebar-link ${isActive ? 'active bg-primary-600' : 'text-grey-300 hover:bg-sidebar-hover'}
                    ${expanded ? '' : 'justify-center px-2'}
                  `}
                  title={!expanded ? item.label : ''}
                  aria-label={!expanded ? item.label : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {expanded && <span className="ml-3">{item.label}</span>}
                </NavLink>
              )
            })}
          </>
        )}
      </nav>
      
      {/* Bottom Section */}
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-grey-700 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <NavLink
          to="/settings"
          className={({ isActive }) => `
            sidebar-link ${isActive ? 'active' : 'text-grey-300 hover:bg-sidebar-hover'}
            ${expanded ? '' : 'justify-center px-2'}
          `}
          title={!expanded ? 'Settings' : ''}
          aria-label={!expanded ? 'Settings' : undefined}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          {expanded && <span className="ml-3">Settings</span>}
        </NavLink>
      </div>
    </aside>
  )
}

export default Sidebar
